import type { CompoundSelectSettingType } from '~/presentation/reports/ui/type';
import type { IControlSettingData, IControlSettingUI } from '../types';

const formatVariable = (id: string, value: unknown) => {
  if (value === null) return;
  return `var ${id} = ${JSON.stringify(value)}`;
};
export const createVariables = (values?: IControlSettingData): string[] => {
  const variables: string[] = [];

  Object.entries(values || {}).forEach(([key, value]) => {
    const formattedVariable = formatVariable(key, value.value);
    formattedVariable && variables.push(formattedVariable);
  });
  return variables;
};

const pushVar = (variables: string[], id: string, value: unknown) => {
  if (value === null) return;
  variables.push(`var ${id} = ${JSON.stringify(value)}`);
};

export const collectControlVariables = (settings: IControlSettingUI[]): string[] => {
  const variables: string[] = [];

  const traverse = (controls: IControlSettingUI[]) => {
    for (const control of controls) {
      const { id, defaultValue, configs, controls } = control;

      pushVar(variables, id, defaultValue);

      for (const config of configs ?? []) {
        pushVar(variables, config.id, config.defaultValue);
      }

      if (controls?.length) traverse(controls);
    }
  };

  traverse(settings);
  return variables;
};

export const findControlById = (
  controls: IControlSettingUI[],
  id: string,
): IControlSettingUI | CompoundSelectSettingType | undefined => {
  for (const control of controls) {
    if (control.id === id) return control;

    const foundConfig = control.configs?.find((cfg) => cfg.id === id);
    if (foundConfig) return foundConfig;

    const foundChild = control.controls && findControlById(control.controls, id);
    if (foundChild) return foundChild;
  }

  return undefined;
};

export function reorderChildInParent(
  parent: IControlSettingUI,
  uid: string,
  newIndex: number,
): IControlSettingUI {
  if (!parent.controls) return parent;

  const controls = [...parent.controls];
  const oldIndex = controls.findIndex((c) => c.uid === uid);
  if (oldIndex === -1 || newIndex < 0 || newIndex >= controls.length) return parent;

  const [moved] = controls.splice(oldIndex, 1);
  controls.splice(newIndex, 0, moved);

  return { ...parent, controls };
}
